'use client';

import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, ServerError, split } from '@apollo/client';
import { PossibleTypesMap } from '@apollo/client/cache';
import { WebSocketLink } from '@apollo/client/link/ws';
import { RetryLink } from '@apollo/client/link/retry';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import { CachePersistor, LocalStorageWrapper } from 'apollo3-cache-persist';
import { getMainDefinition } from 'apollo-utilities';
import { disableFragmentWarnings } from 'graphql-tag';
import { OperationDefinitionNode } from 'graphql';
import { cleanTypenameLink, handleRefreshToken } from '.';
import introspectionQueryResultData from './fragmentTypes.json';
import { redirect } from 'next-common';
import { Routes, apimEndpoint } from '../common';
import { getCachedToken } from '../config/authentication';
import { getAppendingNumericIdFromUrl } from '../utils';

disableFragmentWarnings();

export const getApolloClient = () => {
  const defaultGraphQLEndpoint = process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT;

  const salesCommonGraphqlURL = process.env.NEXT_PUBLIC_SALES_COMMON_GRAPHQL_URL;

  const origin = typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : defaultGraphQLEndpoint;
  let serverGraphqlUrl: string;

  if (apimEndpoint) {
    serverGraphqlUrl = `${apimEndpoint}/graphql`;
  } else if (origin.includes('https://')) {
    serverGraphqlUrl = `${origin}/graphql`;
  } else {
    serverGraphqlUrl = defaultGraphQLEndpoint;
  }

  const SERVER_GRAPHQL_URL = serverGraphqlUrl;
  // tslint:disable-next-line: no-console
  console.log('GraphQL endpoint = ', SERVER_GRAPHQL_URL);

  const introspectionToPossibleTypes = (data): PossibleTypesMap => {
    const possibleTypes = {};

    data.__schema.types.forEach((supertype) => {
      if (supertype.possibleTypes) {
        possibleTypes[supertype.name] = supertype.possibleTypes.map((subtype) => subtype.name);
      }
    });

    return possibleTypes;
  };

  const cache = new InMemoryCache({
    possibleTypes: introspectionToPossibleTypes(introspectionQueryResultData),
    typePolicies: {
      VehicleModel: {
        // Disable cache normalization
        keyFields: false,
      },
    },
  });

  const persistorOptions = {
    cache,
    storage: new LocalStorageWrapper(window?.localStorage),
  };

  const persistor = new CachePersistor(persistorOptions);

  const setUpCache = async () => {
    await persistor.restore();
  };

  // tslint:disable-next-line: no-console
  setUpCache().catch((error) => console.error(error));

  const httpLink = new HttpLink({ uri: SERVER_GRAPHQL_URL });

  const salesCommonLink = new HttpLink({ uri: salesCommonGraphqlURL });

  const wsOptions = () => {
    return {
      uri: SERVER_GRAPHQL_URL?.replace('https', 'wss'),
      options: {
        lazy: true,
        reconnect: true,
        timeout: 10000,
        // connectionParams: {
        //   Authorization: authToken ? `Bearer ${authToken}` : '',
        // },
      },
    };
  };

  const wsLink = new WebSocketLink(wsOptions());

  // ******* NETWORK LINKS *******
  const errorLink = onError(({ graphQLErrors, networkError, operation }: ErrorResponse) => {
    const isUnauthenticated =
      Array.isArray(graphQLErrors) &&
      graphQLErrors.some((e) => e.extensions && e.extensions.code === 'UNAUTHENTICATED');
    const isForbidden =
      Array.isArray(graphQLErrors) && graphQLErrors.some((e) => e.extensions && e.extensions.code === 'FORBIDDEN');
    const statusCode = (networkError?.hasOwnProperty('statusCode') && (networkError as ServerError).statusCode) || null;
    operation.setContext({
      allowedUser: !isForbidden,
    });

    function hasTokenExpired() {
      return !isForbidden && isUnauthenticated;
    }
    // If JWT token is expire refresh it with new token
    if (isUnauthenticated) {
      handleRefreshToken();
    }

    if (statusCode === 400) {
      if (hasTokenExpired() && typeof window !== 'undefined') {
        window.location.reload();
      }
      if (isForbidden) {
        const forbidenRoute = Routes.getForbiddenPage();
        if (typeof window !== 'undefined' && window.location.pathname.includes(forbidenRoute)) return;
        redirect.href = forbidenRoute;
      }
    }

    if (graphQLErrors) {
      graphQLErrors.forEach(({ message }) => {
        // tslint:disable-next-line:no-console
        console.error(`GraphQL Error: ${message}`);
      });
    }
    if (networkError) {
      // tslint:disable-next-line:no-console
      console.error(`Network Error: ${networkError.message}`);
    }
  });

  const retryIf = (error) => {
    const doNotRetryCodes = [500, 400];
    return !!error && !doNotRetryCodes.includes(error.statusCode);
  };

  const retryLink = new RetryLink({
    delay: {
      initial: 100,
      max: 2000,
      jitter: true,
    },
    attempts: {
      max: 5,
      retryIf,
    },
  });

  const backendLink = split(
    // split based on operation type
    ({ query }) => {
      const { kind, operation } = getMainDefinition(query) as OperationDefinitionNode;
      return kind === 'OperationDefinition' && operation === 'subscription';
    },
    wsLink,
    httpLink
  );

  // AUTH-LINK
  const authLink = new ApolloLink((operation, forward) => {
    const token = getCachedToken();
    // Use the setContext method to set the HTTP headers.
    operation.setContext({
      headers: {
        ...operation.getContext().headers,
        Authorization: `Bearer ${token}`,
        'X-Order-Id': `${getAppendingNumericIdFromUrl()}`,
      },
    });

    // Call the next link in the middleware chain.
    return forward(operation);
  });

  const link = ApolloLink.from([cleanTypenameLink, authLink, retryLink, errorLink, backendLink]);
  const salesCommonApolloLink = ApolloLink.from([cleanTypenameLink, authLink, retryLink, errorLink, salesCommonLink]);
  const client = new ApolloClient({
    link: ApolloLink.split(
      (operation) => operation.getContext().clientName === 'sales-common',
      salesCommonApolloLink,
      link
    ),
    cache: new InMemoryCache({
      typePolicies: {
        ServiceContractPriceConfig: {
          keyFields: false,
        },
      },
    }),
    resolvers: {},
    typeDefs: '',
  });

  return client;
};
